import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { supabaseEnabled } from '../../lib/supabase';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const requestPasswordReset = useAuthStore((s) => s.requestPasswordReset);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!supabaseEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
        <Card className="w-full max-w-md border-gray-800 bg-gray-900">
          <CardContent className="p-8 text-center space-y-3">
            <h2 className="text-lg font-semibold text-gray-100">Password reset unavailable</h2>
            <p className="text-sm text-gray-400">
              Self-serve password reset is not enabled on this deployment. Contact support and we&rsquo;ll reset your account.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline" className="border-gray-700 text-gray-200">
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Enter the email you signed up with.');
      return;
    }
    setSending(true);
    const result = await requestPasswordReset(email.trim().toLowerCase());
    setSending(false);
    if (!result.success) {
      setError(result.error || 'Could not send reset link. Try again shortly.');
      return;
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
      <Card className="w-full max-w-md border-gray-800 bg-gray-900">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-emerald-600/20">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-100">Reset your password</CardTitle>
          <CardDescription className="text-gray-400">
            We&rsquo;ll email you a link to set a new password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-gray-300">
                If an account exists for <span className="text-gray-100 font-medium">{email}</span>, a password-reset email is on its way.
                Check your inbox (and spam folder).
              </p>
              <p className="text-xs text-gray-500">
                The link is valid for 1 hour. Didn&rsquo;t get it? Wait a minute, then try again.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="border-red-800 bg-red-950">
                  <AlertDescription className="text-red-300">{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100"
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {sending ? 'Sending\u2026' : 'Send reset link'}
              </Button>
              <div className="text-center text-sm text-gray-400">
                <button type="button" onClick={() => navigate('/login')} className="text-emerald-500 hover:underline">
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
